import { useMemo } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { CONNECTOR_LABEL, addressOf, useConnections } from '@/entities/connection';
import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { SOURCES_PER_PAGE, useConnectionSummaries } from '../api/catalogue';
import { SourceCatalogueCard } from './SourceCatalogueCard';

/* The catalogue's first screen: which source, before which table.
 *
 * **Why this replaced a flat table of every dataset.** The old `/datasets` was one list of every
 * harvested table with a group header per source. That answers "what exists" and buries the question
 * a reader actually arrives with — *which database am I looking at* — under a keyset page whose
 * groups covered only the rows that page happened to include. Two sources may legitimately both hold
 * a `public.employee`, so in a flat list those two rows were indistinguishable without reading the
 * header above them.
 *
 * The drill-down is source → its datasets → one dataset, which is also the shape of the thing: a
 * dataset belongs to the connection it was harvested from, and every level of the URL says so.
 *
 * **Two requests, and the screen renders on the first.** The summaries carry the counts; the
 * connection listing carries the names and addresses. A name that has not arrived yet is a dim
 * sentence rather than a blocked page, because the counts are the reason somebody opened this screen
 * and holding them back for a display name would be the wrong trade.
 *
 * **The order is the Connections screen's**, as the design keeps it: one list of sources in one
 * order everywhere, so a reader who knows where a source sits on one screen finds it in the same
 * place on the other. A source the listing does not know (yet) follows the ones it does.
 */

export function SourceCatalogue(): ReactElement {
  const { t } = useTranslation();
  const summaries = useConnectionSummaries();
  /* The same cached read the Connections screen makes, so arriving here after visiting that screen
   * costs nothing and the two cannot name a source differently. */
  const connections = useConnections();

  const byId = useMemo(
    () => new Map((connections.data?.items ?? []).map((each) => [each.id, each])),
    [connections.data],
  );

  const ordered = useMemo(() => {
    if (!summaries.isSuccess) return [];
    const position = new Map(
      (connections.data?.items ?? []).map((each, index) => [each.id, index]),
    );
    return [...summaries.data.items].sort(
      (a, b) =>
        (position.get(a.connection_id) ?? Number.MAX_SAFE_INTEGER) -
        (position.get(b.connection_id) ?? Number.MAX_SAFE_INTEGER),
    );
  }, [summaries.isSuccess, summaries.data, connections.data]);

  return (
    <>
      <PageHeader
        title={t('catalogue.title')}
        help={t('help.feature.datasets')}
        lede={t('catalogue.subtitle')}
      />

      {summaries.isError ? (
        <Alert tone="error" title={t('catalogue.unreachable')}>
          {summaries.error.message}
        </Alert>
      ) : null}

      {summaries.isPending ? (
        <>
          <p role="status" className="lq-muted">
            {t('catalogue.loading')}
          </p>
          <SkeletonRows rows={4} columns={3} />
        </>
      ) : null}

      {summaries.isSuccess && ordered.length === 0 ? (
        <EmptyState
          title={t('catalogue.emptyTitle')}
          guidance={t('catalogue.emptyGuidance')}
          action={
            /* The one action on this screen, and it leaves it: registering a source is the
               Connections screen's job and duplicating the wizard here would be a second place the
               registration flow has to be kept correct. */
            <Link className="lq-btn lq-btn--primary" to={SCREEN_PATHS.connections}>
              {t('catalogue.emptyAction')}
            </Link>
          }
        />
      ) : null}

      {ordered.length > 0 ? (
        <>
          <div className="lq-sourcegrid">
            {ordered.map((summary) => (
              <SourceCatalogueCard
                key={summary.connection_id}
                summary={summary}
                connection={byId.get(summary.connection_id)}
                /* Loading and failed both mean "a missing name is about us, not about the source".
                   Only a *successful* listing that lacks the id justifies saying the source is not
                   in the listing — the three-causes rule `useDatasetNames`' consumers follow. */
                namesUnread={connections.isPending || connections.isError}
                connectorLabel={(connection) =>
                  CONNECTOR_LABEL[connection.connector_type]
                }
                address={addressOf}
                to={`${SCREEN_PATHS.datasets}/source/${summary.connection_id}`}
              />
            ))}
          </div>

          {/* The cap, stated only when the page is full. Below it the sentence would be noise; at
              exactly it, a reader has no way to tell a complete estate from a truncated one — the
              same case `CatalogBrowser`'s pager spells out for its own boundary. */}
          {ordered.length === SOURCES_PER_PAGE ? (
            <p className="lq-muted">
              {t('catalogue.capped', { count: SOURCES_PER_PAGE })}
            </p>
          ) : null}
        </>
      ) : null}
    </>
  );
}
